import React, { FunctionComponent, useRef } from 'react'
import { ApiTransaction } from '@vend/types'
import { useWebRegisterStatusManager } from '../../../../ng-compat/hooks'
import { LSPAYPaymentModal } from '../../../payments/lspay-payment-modal/components/LSPAYPaymentModal'
import { useHandleSwiperPaymentSuccess } from '../hooks/useHandleSwiperPaymentSuccess'
import { LSPayPaymentOption, SalePayment } from './types'
import { OfflinePayment } from './OfflinePaymentModal'

interface Props {
  paymentOptions: LSPayPaymentOption
  onClose: () => void
  onSuccess: (result: SalePayment) => void
}

export const LsPay: FunctionComponent<Props> = ({
  paymentOptions,
  onClose,
  onSuccess,
}: Props) => {
  const handleSwiperPaymentSuccess = useHandleSwiperPaymentSuccess()
  const isOffline = !useWebRegisterStatusManager().isOnline()
  const isClosing = useRef<boolean>(false)

  if (isOffline) {
    return (
      <OfflinePayment
        paymentName={paymentOptions.paymentType.name}
        onClose={onClose}
      />
    )
  }

  const handleModalClose = async (
    transactionPromise?: Promise<ApiTransaction>
  ): Promise<void> => {
    if (isClosing.current) {
      return
    }

    isClosing.current = true

    if (!transactionPromise) {
      onClose()

      return
    }

    const transaction = await transactionPromise

    const payment = handleSwiperPaymentSuccess({
      transaction,
      paymentType: paymentOptions.paymentType,
      amountTendered: paymentOptions.amount,
    })

    if (!payment) {
      onClose()

      return
    }

    onSuccess(payment)
  }

  return (
    <LSPAYPaymentModal
      amount={paymentOptions.amount}
      outletId={paymentOptions.outletId}
      currency={paymentOptions.currency}
      register={paymentOptions.register}
      registerSaleId={paymentOptions.registerSaleId}
      originalRegisterSalePaymentIds={
        paymentOptions.originalRegisterSalePaymentIds
      }
      originalSalePaidByOtherPT={paymentOptions.originalSalePaidByOtherPT}
      originalSaleHasProcessingServicePayment={
        paymentOptions.originalSaleHasProcessingServicePayment
      }
      originalLspayPaymentAmount={paymentOptions.originalLspayPaymentAmount}
      retailerPaymentType={paymentOptions.paymentType}
      transactionId={paymentOptions.transactionId}
      paymentIntentId={paymentOptions.paymentIntentId}
      isRecovery={paymentOptions.isRecovery}
      withSurcharge={paymentOptions.withSurcharge}
      tipEligibleAmount={paymentOptions.tipEligibleAmount}
      // Force the LSPAYPaymentModal to show a single flow if requested.
      forceFlow={paymentOptions.lspayFlow ?? undefined}
      // PMOF specifics
      paymentMethodOnFileId={paymentOptions.paymentMethodOnFileId}
      close={handleModalClose}
    />
  )
}
